import { HttpClient, HttpEventType } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Guid } from '@app/core/constants/constants';
import { ImageService } from '@app/shared/services/image.service';
import { TranslateService } from '@ngx-translate/core';
import { base64ToFile, Dimensions, ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';
import { Subscription } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UploadState } from '../../models/enums';
import { GlobalVariablesService } from '@app/core/services/global-variables.service';
import { ImageUploaderMode, resolveImageUploaderId } from './image-uploader-id';

export type { ImageUploaderMode } from './image-uploader-id';

export interface ImageUploadResult {
    imageId: Guid;
    imageUrl: string;
    file?: File;
}

@Component({
    selector: 'app-image-uploader',
    templateUrl: './image-uploader.component.html',
    styleUrls: ['./image-uploader.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ImageUploaderComponent implements OnInit, OnChanges {


    @ViewChild('fileInput', { static: false }) fileInput: ElementRef;

    @Input()
    public mode: ImageUploaderMode = 'profile';

    @Input()
    public imageId: Guid;

    @Input()
    public imageUrl: string;

    @Input()
    public accept: string = 'image/*';

    @Output()
    public imageUploaded: EventEmitter<ImageUploadResult> = new EventEmitter<ImageUploadResult>();

    @Output()
    public imageRemoved: EventEmitter<void> = new EventEmitter<void>();

    imageChangedEvent: any = '';

    croppedImage: any = '';
    canvasRotation = 0;
    rotation = 0;
    scale = 1;
    showCropper = true;
    containWithinAspectRatio = false;
    transform: ImageTransform = {};
    fileSelected: boolean = false;
    private _aiCrop: boolean = true;


    progress: number;
    file: File;
    croppedFile: File;

    sourceImageUrl: string = '';
    previewImageUrl: string = '';
    uploadState: UploadState = UploadState.none;
    uploadStates = UploadState;
    successMessage: string;
    uploadError: string;
    readonly fileInputId = `image-upload-${Guid.newGuid()}`;

    // Mobile tab navigation
    activeTab: 'preview' | 'upload' = 'upload';

    public set aiCrop(value: boolean) {
        this._aiCrop = value;
        this.cancelUpload();

        if (value) {
            this.uploadFile();
        }
    }

    public get aiCrop(): boolean {
        return this._aiCrop;
    }

    private uploadSubscription: Subscription;
    private readonly dialogData = inject<unknown>(MAT_DIALOG_DATA, { optional: true });
    private readonly dialogRef = inject<MatDialogRef<ImageUploaderComponent> | null>(MatDialogRef, { optional: true });
    public readonly translateService = inject(TranslateService);
    private readonly http = inject(HttpClient);
    private readonly imageService = inject(ImageService);
    private readonly globals = inject(GlobalVariablesService);
    private readonly cd = inject(ChangeDetectorRef);



    @Output()
    public imageUpdated: EventEmitter<string> = new EventEmitter<string>();

    ngOnInit(): void {
        const initialImageId = resolveImageUploaderId(this.mode, this.imageId, this.dialogData);
        if (initialImageId) {
            this.imageId = initialImageId as unknown as Guid;
        }

        // Load existing image if available
        if (this.isProfileMode && this.imageId) {
            this.setPreviewImageUrl(this.imageService.getImageUrl(this.imageId, 0.8));
        }

        if (this.isDirectMode && this.imageUrl) {
            this.setPreviewImageUrl(this.imageUrl);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.isDirectMode && changes.imageUrl) {
            this.setPreviewImageUrl(this.imageUrl ?? '');
        }
    }

    get isDirectMode(): boolean {
        return this.mode === 'direct';
    }

    get isProfileMode(): boolean {
        return !this.isDirectMode;
    }

    fileChangeEvent(event: any): void {

        this.cancelUpload();

        this.file = event.target.files[0];
        if (!this.file) {
            return;
        }

        this.fileSelected = this.file != null;
        this.imageChangedEvent = event;

        // Switch to upload tab on mobile when file is selected
        if (this.fileSelected && window.innerWidth < 1024) {
            this.activeTab = 'upload';
        }

        this.loadFile();
    }

    onDrop(files: File[]) {
        this.cancelUpload();
        this.file = files[0];
        if (!this.file) {
            return;
        }

        this.fileSelected = this.file != null;
        
        // Switch to upload tab on mobile when file is selected
        if (this.fileSelected && window.innerWidth < 1024) {
            this.activeTab = 'upload';
        }
        
        this.loadFile();
    }

    loadFile() {
        this.uploadError = undefined;

        const reader = new FileReader();
        reader.readAsDataURL(this.file);
        reader.onload = (e) => {
            const nextUrl = reader.result as string;
            this.sourceImageUrl = nextUrl;
            this.setPreviewImageUrl(nextUrl);
        };

        if (this.isDirectMode || this.aiCrop)
            this.uploadFile();


    }

    imageCropped(event: ImageCroppedEvent) {
        this.croppedImage = event.base64;
        this.setPreviewImageUrl(event.base64);
        this.croppedFile = new File([base64ToFile(event.base64)], this.file.name, { type: this.file.type });
        this.uploadState = UploadState.pending;
    }

    imageLoaded(): void {
        this.showCropper = true;
        console.log('Image loaded');
    }

    cropperReady(sourceImageDimensions: Dimensions) {
        console.log('Cropper ready', sourceImageDimensions);
    }

    loadImageFailed() {
        console.log('Load failed');
    }

    rotateLeft() {
        this.canvasRotation--;
        this.flipAfterRotate();
    }

    rotateRight() {
        this.canvasRotation++;
        this.flipAfterRotate();
    }

    private flipAfterRotate() {
        const flippedH = this.transform.flipH;
        const flippedV = this.transform.flipV;
        this.transform = {
            ...this.transform,
            flipH: flippedV,
            flipV: flippedH
        };
    }


    flipHorizontal() {
        this.transform = {
            ...this.transform,
            flipH: !this.transform.flipH
        };
    }

    flipVertical() {
        this.transform = {
            ...this.transform,
            flipV: !this.transform.flipV
        };
    }

    resetImage() {
        this.scale = 1;
        this.rotation = 0;
        this.canvasRotation = 0;
        this.transform = {};
    }

    zoomOut() {
        this.scale -= .1;
        this.transform = {
            ...this.transform,
            scale: this.scale
        };
    }

    zoomIn() {
        this.scale += .1;
        this.transform = {
            ...this.transform,
            scale: this.scale
        };
    }

    toggleContainWithinAspectRatio() {
        this.containWithinAspectRatio = !this.containWithinAspectRatio;
    }

    updateRotation() {
        this.transform = {
            ...this.transform,
            rotate: this.rotation
        };
    }

    deleteImage() {
        this.fileSelected = false;
        this.croppedImage = '';
        this.sourceImageUrl = '';
        this.previewImageUrl = '';
        this.uploadError = undefined;
        this.imageRemoved.emit();
    }

    uploadFile() {


        this.cancelUpload();

        if (this.isDirectMode || this.aiCrop)
            this.croppedFile = this.file;

        //null check
        if (!this.croppedFile) {
            return;
        }

        const imageId = this.ensureImageId();
        const formData = new FormData();
        formData.append('file', this.croppedFile);
        formData.append('model', JSON.stringify({
            id: imageId,
            name: this.croppedFile.name,
            keepDuration: 3
        }));


        this.uploadState = UploadState.uploading;
        this.uploadError = undefined;
        this.uploadSubscription = this.http.post(`${this.globals.photoServerApiAddress}/image`, formData, {
            reportProgress: true,
            observe: 'events'
        })
            .subscribe(event => {
                if (event.type === HttpEventType.UploadProgress) {
                    this.progress = Math.round(event.loaded / event.total * 100);
                }

            }, error => {
                this.uploadState = UploadState.pending;
                this.uploadError = 'Upload failed.';
                console.log('Upload failed.', error);
            }, () => {
                // upload complete event
                this.uploadState = UploadState.uploaded;

                // Switch to preview tab on mobile when upload is complete
                if (window.innerWidth < 1024) {
                    this.activeTab = 'preview';
                }

                const uploadedImageUrl = this.getUploadedImageUrl();
                this.setPreviewImageUrl(uploadedImageUrl);
                this.imageUpdated.emit(uploadedImageUrl);

                if (this.isDirectMode) {
                    this.imageUploaded.emit({
                        imageId,
                        imageUrl: uploadedImageUrl,
                        file: this.croppedFile
                    });
                }
            });
    }

    messageHandle: any;
    showSuccessMessage(message: string, timeout: number = 2000) {
        if (this.messageHandle)
            clearTimeout(this.messageHandle);

        this.successMessage = message;

        this.messageHandle = setTimeout(() => {
            this.successMessage = undefined;
        }, timeout);
    }


    loadImage() {
        this.setPreviewImageUrl(this.getUploadedImageUrl());
    }

    private setPreviewImageUrl(nextUrl: string): void {
        Promise.resolve().then(() => {
            this.previewImageUrl = nextUrl ?? '';
            this.cd.markForCheck();
        });
    }


    public cancelUpload() {
        this.uploadState = UploadState.pending;
        this.progress = 0;
        if (this.uploadSubscription) {
            this.uploadSubscription.unsubscribe();
            this.uploadSubscription = null;
        }
    }

    close() {
        if (this.uploadState == UploadState.uploading)
            this.cancelUpload();

        if (this.uploadState == UploadState.uploaded)
            this.dialogRef?.close({ imageUrl: this.previewImageUrl, imageId: this.imageId });
        else
            this.dialogRef?.close();
    }

    private ensureImageId(): Guid {
        if (!resolveImageUploaderId(this.mode, this.imageId, null)) {
            this.imageId = Guid.newGuid();
        }

        return this.imageId;
    }

    private getUploadedImageUrl(): string {
        return this.imageService.getImageUrl(
            this.ensureImageId(),
            this.isDirectMode ? 0 : (this.aiCrop ? 0.8 : 0),
            Guid.newGuid(),
            this.isDirectMode ? 2400 : 256
        );
    }

}
